// src/pages/VehicleV2/VehicleTransferEntryListing.jsx
// ASPX: VehicleTransferEntryListing.aspx + .cs
// Pick eligible own vehicle / spare, then start a NEW transfer.
// No page-rights flow.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaSyncAlt,
  FaArrowLeft,
  FaExchangeAlt,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/vehicleTransferEntryListing`;

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const defaultFilters = {
  filterType: "own",
  selectedBranchId: "Select",
  storeId: "Select",
  vehicleTypeIndex: "Select",
  vehicleCode: "",
  vehicleName: "",
  vehicleRegNo: "",
};

const SuggestInput = ({
  name,
  value,
  onChange,
  disabled,
  placeholder,
  className,
  suggestions,
}) => (
  <div className="relative">
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete="off"
      list={`${name}-list`}
    />
    <datalist id={`${name}-list`}>
      {(suggestions || []).slice(0, 12).map((s, i) => (
        <option key={`${s}-${i}`} value={s} />
      ))}
    </datalist>
  </div>
);

const VehicleTransferEntry = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid") || "";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || "0";

  const branchId = useMemo(() => String(toInt(branchIdLS, 0)), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim() || "HO",
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim() || "0", [userIdLS]);
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") || userId;

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [branchLocked, setBranchLocked] = useState(branchType !== "HO");

  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [suggest, setSuggest] = useState({
    vehicleCode: [],
    vehicleName: [],
    vehicleRegNo: [],
  });

  const suggestTimer = useRef(null);
  const isBusy = pageLoading || dropdownLoading || gridLoading;

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const loadDropdowns = useCallback(
    async (selectedBranchIdValue) => {
      try {
        setDropdownLoading(true);
        const { data } = await axios.get(`${API_BASE}/dropdowns`, {
          params: {
            branchId,
            branchType,
            loginUserId,
            selectedBranchId: selectedBranchIdValue || "Select",
          },
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Dropdown failed");
        setBranches(data.branches || []);
        setStores(data.stores || []);
        setVehicleTypes(data.vehicleTypes || []);
        setBranchLocked(!!data.branchLocked);
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Dropdown failed");
      } finally {
        setDropdownLoading(false);
      }
    },
    [branchId, branchType, loginUserId, commonHeaders]
  );

  const loadList = useCallback(
    async ({ silent = false, activeFilters = defaultFilters } = {}) => {
      if (!toInt(loginUserId) || !toInt(branchId)) {
        if (!silent) toast.error("branchId / loginUserId missing");
        return;
      }
      try {
        setGridLoading(true);
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            filterType: activeFilters.filterType,
            branchId,
            branchType,
            loginUserId,
            selectedBranchId: activeFilters.selectedBranchId || "Select",
            storeId: activeFilters.storeId || "Select",
            vehicleTypeIndex: activeFilters.vehicleTypeIndex || "Select",
            vehicleCode: clean(activeFilters.vehicleCode),
            vehicleName: clean(activeFilters.vehicleName),
            vehicleRegNo: clean(activeFilters.vehicleRegNo),
          },
          headers: commonHeaders,
        });
        if (!data?.success) {
          setRows([]);
          setPage(1);
          if (!silent) toast.warn(data?.message || "No data found");
          return;
        }
        setRows(Array.isArray(data.list) ? data.list : []);
        setPage(1);
        if (!silent) toast.success(`Found ${data.count ?? 0} record(s)`);
      } catch (err) {
        setRows([]);
        setPage(1);
        toast.error(err?.response?.data?.message || err.message || "List failed");
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, branchType, commonHeaders]
  );

  const loadInit = useCallback(async () => {
    setPageLoading(true);
    await loadDropdowns("Select");
    await loadList({ silent: true, activeFilters: defaultFilters });
    setPageLoading(false);
  }, [loadDropdowns, loadList]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const fetchSuggest = useCallback(
    async (kind, prefix) => {
      if (!prefix || filters.filterType === "spares") return;
      try {
        const { data } = await axios.get(`${API_BASE}/autocomplete`, {
          params: {
            kind,
            prefix,
            branchId,
            branchType,
            loginUserId,
            storeId: filters.storeId,
          },
          headers: commonHeaders,
        });
        const key =
          kind === "code"
            ? "vehicleCode"
            : kind === "name"
            ? "vehicleName"
            : "vehicleRegNo";
        setSuggest((p) => ({ ...p, [key]: data.list || [] }));
      } catch {
        /* ignore suggest errors */
      }
    },
    [filters.filterType, filters.storeId, branchId, branchType, loginUserId, commonHeaders]
  );

  const onFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setFilters((prev) => {
        const next = { ...prev, [name]: value };
        if (name === "filterType" && value === "spares") {
          next.vehicleName = "";
          next.vehicleRegNo = "";
        }
        if (name === "selectedBranchId") next.storeId = "Select";
        return next;
      });

      if (name === "selectedBranchId") loadDropdowns(value);

      if (["vehicleCode", "vehicleName", "vehicleRegNo"].includes(name)) {
        clearTimeout(suggestTimer.current);
        const kind =
          name === "vehicleCode" ? "code" : name === "vehicleName" ? "name" : "regno";
        suggestTimer.current = setTimeout(() => fetchSuggest(kind, value), 250);
      }
    },
    [loadDropdowns, fetchSuggest]
  );

  const handleSearch = useCallback(async () => {
    setHasSearched(true);
    await loadList({ silent: false, activeFilters: filters });
  }, [loadList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = { ...defaultFilters, filterType: filters.filterType };
    setFilters(reset);
    setHasSearched(true);
    await loadDropdowns(reset.selectedBranchId);
    await loadList({ silent: false, activeFilters: reset });
  }, [filters.filterType, loadDropdowns, loadList]);

  const handleChangeBranch = useCallback(
    async (row) => {
      const isSpares = filters.filterType === "spares";

      if (isSpares) {
        const spareCatId = row?.spareCatId || row?.id;
        if (!spareCatId) {
          toast.error("SpareCatID not found");
          return;
        }
        navigate("/vehicle/sparestransfer", {
          state: {
            VTflag: false,
            SpareCatID: spareCatId,
            SpareCatId: spareCatId,
            Tobrnch: "",
            reason: "",
            row,
            filterType: "spares",
          },
        });
        return;
      }

      const vehicleId = row?.vehicleId || row?.id;
      if (!vehicleId) {
        toast.error("VehicleId not found in selected row");
        return;
      }

      try {
        const { data } = await axios.get(`${API_BASE}/precheck-transfer`, {
          params: { vehicleId },
          headers: commonHeaders,
        });
        if (data?.success && data?.canTransfer === false) {
          toast.warning(data?.reason || "Transfer not allowed");
          return;
        }
        navigate("/vehicle/vehicletransfer", {
          state: {
            VTflag: false,
            VehicleID: vehicleId,
            vehicleId,
            row,
            filterType: "own",
          },
        });
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Precheck failed");
      }
    },
    [filters.filterType, navigate, commonHeaders]
  );

  const isSpares = filters.filterType === "spares";

  const totalRecords = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize) || 1);
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const pageStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageRows = useMemo(
    () => rows.slice(pageStart, pageStart + pageSize),
    [rows, pageStart, pageSize]
  );
  const pageEnd = pageStart + pageRows.length;

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    const list = [];
    for (let i = start; i <= end; i += 1) list.push(i);
    return list;
  }, [currentPage, totalPages]);

  const pagerBtn = (active) =>
    `min-w-[36px] h-9 px-3 rounded-lg text-sm font-semibold border ${
      active
        ? `text-white ${buttonClass} border-transparent`
        : isDark
        ? "bg-gray-700 text-white border-gray-600 hover:bg-gray-600"
        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
    }`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TRANSFER</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Transfer Entry Listing
              </h2>
              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                INFRA SCREEN : B-23 • Branch {branchId || "-"} • {branchType} •{" "}
                {isSpares ? "Spares & Catalogue" : "Own Vehicle"} • Records {totalRecords}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={loadInit}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className={isBusy ? "animate-spin" : ""} />
                Refresh
              </button>
              <button
                type="button"
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200"
                        : "border-gray-300 text-gray-700"
                    }`}
                  >
                    <FaEye /> View All
                  </button>
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold text-white ${buttonClass} ${hoverClass} ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch /> {gridLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className={labelClass}>Vehicle Type</label>
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="filterType"
                      value="own"
                      checked={!isSpares}
                      onChange={onFilterChange}
                      disabled={isBusy}
                    />
                    <span>Own Vehicle</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="filterType"
                      value="spares"
                      checked={isSpares}
                      onChange={onFilterChange}
                      disabled={isBusy}
                    />
                    <span>Spares And Catelogue</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="selectedBranchId"
                    value={filters.selectedBranchId}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={branchLocked || isBusy}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b, idx) => (
                      <option
                        key={b.fld_Branchid ?? idx}
                        value={String(b.fld_Branchid ?? b.id ?? "")}
                      >
                        {b.fld_BranchName ?? b.name ?? b.fld_Branchid}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="Select">Select</option>
                    {stores.map((s, idx) => (
                      <option
                        key={s.fld_storeid ?? idx}
                        value={String(s.fld_storeid ?? s.id ?? "")}
                      >
                        {s.fld_storename ?? s.name ?? s.fld_storeid}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleTypeIndex"
                    value={filters.vehicleTypeIndex}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="Select">Select</option>
                    {vehicleTypes.map((t, idx) => (
                      <option key={t.id ?? idx} value={String(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <SuggestInput
                    name="vehicleRegNo"
                    value={filters.vehicleRegNo}
                    onChange={onFilterChange}
                    className={inputClass}
                    placeholder="Enter registration number"
                    disabled={isSpares || isBusy}
                    suggestions={suggest.vehicleRegNo}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <SuggestInput
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={onFilterChange}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    disabled={isBusy}
                    suggestions={suggest.vehicleCode}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <SuggestInput
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={onFilterChange}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                    disabled={isSpares || isBusy}
                    suggestions={suggest.vehicleName}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || gridLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                Loading...
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                {hasSearched ? "No Records Found!" : "Please search to view records"}
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm">S.No</th>
                    <th className="px-4 py-3 text-left text-sm">Vehicle Main Type</th>
                    <th className="px-4 py-3 text-left text-sm">Vehicle Code</th>
                    <th className="px-4 py-3 text-left text-sm">
                      {isSpares ? "Description" : "Vehicle Name"}
                    </th>
                    <th className="px-4 py-3 text-left text-sm">
                      {isSpares ? "SL.No" : "Vehicle Registration Number"}
                    </th>
                    <th className="px-4 py-3 text-center text-sm">Changing Branch</th>
                  </tr>
                </thead>
                <tbody className={isDark ? "divide-gray-700 divide-y" : "divide-gray-200 divide-y"}>
                  {pageRows.map((row, idx) => (
                    <tr key={`${row.id}-${pageStart + idx}`} className={isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}>
                      <td className="px-4 py-3 text-sm">{pageStart + idx + 1}</td>
                      <td className="px-4 py-3 text-sm">{row.mainType || "-"}</td>
                      <td className="px-4 py-3 text-sm font-medium">{row.code || "-"}</td>
                      <td className="px-4 py-3 text-sm">
                        {isSpares ? row.description || "-" : row.name || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isSpares ? row.slNo || "-" : row.regNo || "-"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => handleChangeBranch(row)}
                          disabled={isBusy}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white ${buttonClass} ${hoverClass}`}
                        >
                          <FaExchangeAlt className="w-3 h-3" />
                          Change
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {totalRecords > 0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Showing {pageStart + 1} - {pageEnd} of {totalRecords}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Rows
                </label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value) || 10);
                    setPage(1);
                  }}
                  className={`${inputClass} w-24 py-1`}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`${pagerBtn(false)} disabled:opacity-40`}
                  title="Previous"
                >
                  <FaChevronLeft />
                </button>
                {pageNumbers[0] > 1 && (
                  <>
                    <button type="button" onClick={() => setPage(1)} className={pagerBtn(currentPage === 1)}>
                      1
                    </button>
                    {pageNumbers[0] > 2 && <span className="px-1">…</span>}
                  </>
                )}
                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={pagerBtn(n === currentPage)}
                  >
                    {n}
                  </button>
                ))}
                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                      <span className="px-1">…</span>
                    )}
                    <button
                      type="button"
                      onClick={() => setPage(totalPages)}
                      className={pagerBtn(currentPage === totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={`${pagerBtn(false)} disabled:opacity-40`}
                  title="Next"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-start mt-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                isDark
                  ? "bg-gray-700 text-white border-gray-600"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              }`}
            >
              <FaArrowLeft /> Back
            </button>
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTransferEntry;